import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { SearchTypeEnum } from '@/itemFinder/itemFinder.constants';
import { base64guid } from '@/utilities/guid.utilities';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import { OperatorEnum } from '@seeqdev/api/model/PropertyFilterInputV1';
import { SearchTypeEnum as ScalingTableSearchTypeEnum } from '@seeqdev/api/model/ScalingTableUUIDColumnSearchV1';
import type {
  AIPayload,
  AncestorPredicate,
  FixedListSearch,
  HierarchyLevelColumn,
  HierarchySearch,
  Property,
  ScalingTableSearch,
  Search,
  SearchType,
  StoredTableSearch,
  Value,
} from '@/itemFinder/itemFinder.constants';
import type { HierarchyPath, PropertiesList } from '@/itemFinder/itemFinder.store';
import type { SelectedSearchItem } from '@/search/search.types';
import type { AncestorFilterInputV1 } from '@seeqdev/api/model/AncestorFilterInputV1';
import type { FixedListFilterInputV1 } from '@seeqdev/api/model/FixedListFilterInputV1';
import type { FixedListSearchV1 } from '@seeqdev/api/model/FixedListSearchV1';
import type { HierarchySearchOutputV1 } from '@seeqdev/api/model/HierarchySearchOutputV1';
import type { HierarchySearchV1 } from '@seeqdev/api/model/HierarchySearchV1';
import type { PropertyFilterInputV1 } from '@seeqdev/api/model/PropertyFilterInputV1';
import type { PropertyInputV1 } from '@seeqdev/api/model/PropertyInputV1';
import type { PropertySearchV1 } from '@seeqdev/api/model/PropertySearchV1';
import type { ScalingTableUUIDColumnSearchV1 } from '@seeqdev/api/model/ScalingTableUUIDColumnSearchV1';
import type { StoredTableSearchV1 } from '@seeqdev/api/model/StoredTableSearchV1';
import type * as models from '@seeqdev/api/model/models';

/** Sentinel for globally scoped (null scopedTo) in WITHIN predicates. */
export const SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE = '__NULL__';

export const createDefaultStoredTableNameProperty = (searchId: string): Property => {
  const propertyId = base64guid();
  const valueId = base64guid();
  return {
    propertyId,
    searchId,
    propertyName: SeeqNames.Properties.Name,
    operator: OperatorEnum.EQUALS,
    values: [{ value: '', propertyId, searchId, valueId }],
    isColumnSelect: true,
  };
};

type StoredTableSimpleSearchCandidate = {
  predicates?: Property[];
  ancestorPredicates?: unknown[];
  ancestorId?: string;
};

/** Predicates that represent user-configured filters (excludes Scoped To and empty placeholders). */
export const getStoredTableUserPredicates = (predicates: Property[] | undefined): Property[] =>
  (predicates ?? []).filter(
    (property) => property.propertyName && property.propertyName !== SeeqNames.Properties.ScopedTo,
  );

/** Infers simple mode from search shape (single Name column). */
export const isStoredTableSimpleSearch = (search: StoredTableSimpleSearchCandidate): boolean => {
  const userPredicates = getStoredTableUserPredicates(search.predicates);

  if (userPredicates.length !== 1 || search.ancestorPredicates?.length || search.ancestorId) {
    return false;
  }

  const predicate = userPredicates[0];
  return (
    predicate.propertyName === SeeqNames.Properties.Name &&
    predicate.operator === OperatorEnum.EQUALS &&
    !!predicate.columnId
  );
};

export const findScopedToProperty = (predicates: Property[] | undefined): Property | undefined =>
  predicates?.find((property) => property.propertyName === SeeqNames.Properties.ScopedTo);

export const isScopedToLimitedToCurrentWorkbook = (
  scopedToProperty: Property | undefined,
  workbookId: string | undefined,
): boolean =>
  !!scopedToProperty &&
  !!workbookId &&
  scopedToProperty.operator === OperatorEnum.EQUALS &&
  scopedToProperty.values.length === 1 &&
  scopedToProperty.values[0]?.value === workbookId;

export const buildScopedToPropertyValues = (
  limitToCurrentWorkbook: boolean,
  workbookId: string,
  propertyId: string,
  searchId: string | number,
): Value[] => {
  if (limitToCurrentWorkbook) {
    return [
      {
        value: workbookId,
        propertyId,
        searchId: searchId as string,
        valueId: base64guid(),
      },
    ];
  }

  return [
    {
      value: SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE,
      propertyId,
      searchId: searchId as string,
      valueId: base64guid(),
    },
    {
      value: workbookId,
      propertyId,
      searchId: searchId as string,
      valueId: base64guid(),
    },
  ];
};

export const searchSupportsScopedToPredicate = (search: Search): boolean =>
  search.searchType === SearchTypeEnum.PROPERTY || search.searchType === SearchTypeEnum.STOREDTABLE;

/** Whether the limit-to-current-workbook UI toggle is shown for this search. */
export const searchShowsScopedToLimitToggle = (search: Search, tableScopedTo: string | undefined): boolean => {
  // A Global Table Definition (undefined tableScopedTo) has no Analysis to limit the search to
  if (!tableScopedTo) {
    return false;
  }
  if (search.searchType === SearchTypeEnum.PROPERTY) {
    return true;
  }
  if (search.searchType === SearchTypeEnum.STOREDTABLE) {
    return !(search as StoredTableSearch).isSimpleSearch;
  }
  return false;
};

export const formatPropertyFilterValues = (
  propertyName: string,
  operator: string,
  localValues: { value: string | number }[],
): { value: string | number | null }[] =>
  localValues.map((v) => {
    if (
      propertyName === SeeqNames.Properties.ScopedTo &&
      operator === OperatorEnum.WITHIN &&
      (v.value === SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE || v.value === '')
    ) {
      return { value: null };
    }
    if (!v.value && propertyName === SeeqNames.Properties.ScopedTo) {
      // If executing an existing item finder with a workbench search without changing the value, use the existing value
      return { value: localValues[0]?.value ?? null };
    }
    return { value: v.value };
  });

// API payload formatting

/**
 * Filters out hidden exclusion predicates from a property list, so that they can be replaced by
 * the canonical hidden exclusion predicates passed explicitly. This prevents duplicates when the
 * hiddenExclusionPredicates are spread back in.
 */
export const filterHiddenExclusions = (properties: any[], hiddenExclusions: PropertyFilterInputV1[]) =>
  properties.filter(
    (property) =>
      !hiddenExclusions.some(
        (exclusion) =>
          property.propertyName === exclusion.propertyName &&
          property.operator === exclusion.operator &&
          property.values?.[0]?.value === exclusion.values?.[0]?.value,
      ),
  );

export function formatFixedListSearches(searches: FixedListSearch[]): FixedListSearchV1[] | undefined {
  return searches.map((search) => {
    const itemIds = search.itemIds.map((item: any) => (item.id ? item.id : item));

    return {
      searchType: search.searchType,
      isInclude: search.isInclude,
      itemIds,
      searchOrder: search.searchOrder,
    };
  });
}

export function formatHierarchySearches(
  searches: HierarchySearch[],
  hiddenExclusionPredicates: PropertyFilterInputV1[],
): HierarchySearchV1[] | undefined {
  return searches.map((search) => {
    const localPredicates = (search.predicates ?? []).filter((property) => !!property.propertyName);
    const userPredicates = filterHiddenExclusions(localPredicates, hiddenExclusionPredicates);

    return {
      searchType: search.searchType,
      isInclude: search.isInclude,
      hierarchyId: search.hierarchyId,
      searchOrder: search.searchOrder,
      predicates: [...(formatProperties(userPredicates) ?? []), ...hiddenExclusionPredicates],
      ancestorPredicates: formatAncestorPredicates(search.ancestorPredicates ?? []),
      level: search.level,
      fixedListPredicates: formatHierarchyFixedListSearches(search.hierarchyPaths ?? []),
    };
  });
}

export function formatStoredTableSearches(
  searches: StoredTableSearch[],
  hiddenExclusionPredicates: PropertyFilterInputV1[],
): StoredTableSearchV1[] {
  return searches.map((search) => {
    const localPredicates = (search.predicates ?? []).filter((property) => property.propertyName !== '');
    const userPredicates = filterHiddenExclusions(localPredicates, hiddenExclusionPredicates);

    return {
      searchType: SearchTypeEnum.STOREDTABLE,
      isInclude: search.isInclude,
      storedTableId: search.storedTableId,
      ancestorId: search.ancestorId,
      predicates: [...(formatTableProperties(userPredicates) ?? []), ...hiddenExclusionPredicates],
      types: search.types,
      ancestorPredicates: [...formatAncestorPredicates(search.ancestorPredicates ?? [])],
      searchOrder: search.searchOrder,
    };
  });
}

export function formatValues(values: { value: string | number | null }[]): { value: string | number | null }[] {
  return values.map((value) => ({
    value: value.value,
  }));
}

export function formatProperties(properties: any): any[] | undefined {
  return properties.map(
    (property: {
      operator: string;
      propertyName: { value: string };
      propertyId: string;
      values: [{ value: string | number }];
    }) => {
      const propertyName = property.propertyName.value ?? property.propertyName;
      const localValues = property.values ?? [];
      return {
        operator: property.operator,
        propertyName,
        values: formatValues(formatPropertyFilterValues(propertyName, property.operator, localValues)),
      };
    },
  );
}

export function formatTableProperties(properties: any): any[] | undefined {
  return properties.map(
    (property: {
      operator: string;
      propertyName: { value: string };
      propertyId: string;
      values: [{ value: string | number }];
      columnId: string;
      isColumnSelect: boolean;
    }) => {
      const propertyName = property.propertyName.value ?? property.propertyName;
      const localValues = property.values ?? [];
      return {
        operator: property.operator,
        propertyName,
        values: property.isColumnSelect
          ? []
          : formatValues(formatPropertyFilterValues(propertyName, property.operator, localValues)),
        columnId: property.isColumnSelect ? property.columnId : undefined,
      };
    },
  );
}

export function formatAncestorPredicates(ancestorPredicates: AncestorPredicate[]): AncestorFilterInputV1[] {
  return ancestorPredicates
    .filter((ancestorPredicate) => (ancestorPredicate.predicates ?? []).some((property) => !!property.propertyName))
    .map((ancestorPredicate) => {
      const localPredicates = (ancestorPredicate.predicates ?? []).filter((property) => !!property.propertyName);

      return {
        predicates: formatTableProperties(localPredicates) ?? [],
        level: ancestorPredicate.level,
      };
    });
}

export function formatHierarchyFixedListSearches(hierarchyPaths: HierarchyPath[]): FixedListFilterInputV1[] {
  return hierarchyPaths.map((hierarchyPath) => ({
    nodePaths: hierarchyPath.nodes.map((node) => ({
      columnId: node.columnId,
      value: { value: node.value },
    })),
  }));
}

export function formatPropertySearches(
  searches: any,
  options: {
    types: string[] | undefined;
    hiddenExclusionPredicates: PropertyFilterInputV1[];
  },
): PropertySearchV1[] | undefined {
  return searches.map(
    (search: {
      searchType: string;
      isInclude: boolean;
      folderId: { id: string };
      ancestorId: { id: string };
      types: any;
      searchId: string;
      predicates: any;
      ancestorPredicates?: AncestorPredicate[];
      searchOrder?: number;
    }) => {
      // Filter out invalid predicates. A predicate is valid only when propertyName is truthy.
      // When the Search is initialized, we add an empty predicate to show the predicate form in the UI,
      // but these empty predicates (with undefined/empty propertyName) should not be sent to the backend.
      const localPredicates = (search.predicates ?? []).filter((property: PropertiesList) => property.propertyName);
      const userPredicates = filterHiddenExclusions(localPredicates, options.hiddenExclusionPredicates);

      return {
        searchType: SearchTypeEnum.PROPERTY, // Always property search, even if the search option is folder
        isInclude: search.isInclude,
        folderId: search.folderId?.id ?? search.folderId,
        ancestorId: search.ancestorId?.id ?? search.ancestorId,
        predicates: [...(formatProperties(userPredicates) ?? []), ...options.hiddenExclusionPredicates],
        types: options.types ?? search.types,
        ancestorPredicates: formatAncestorPredicates(search.ancestorPredicates ?? []),
        searchOrder: search.searchOrder,
      };
    },
  );
}

/**
 * Converts a list of HierarchyPaths into SelectedSearchItems with proper ancestor chains and IDs.
 *
 * @param hierarchyPaths - The list of hierarchy paths to convert.
 * @param hierarchyLevelColumns - The level column definitions for the hierarchy.
 * @param searchItemTable - The item table selected for this search, used to determine UUID columns.
 * @returns An array of SelectedSearchItems representing the selected nodes.
 */
export const getHierarchySelectedNodes = (
  hierarchyPaths: HierarchyPath[],
  hierarchyLevelColumns: HierarchyLevelColumn[],
  searchItemTable: SelectedSearchItem | undefined,
): SelectedSearchItem[] => {
  function buildUniqueHierarchyNodeId(path: HierarchyPath): string {
    return path.nodes.reduce((generatedId, node) => {
      const pathColumn = hierarchyLevelColumns.find((col) => col.columnId === node.columnId);

      if (pathColumn?.columnType === ColumnTypeEnum.UUID || node.columnId === searchItemTable?.columnId) {
        return node.value!;
      } else if (pathColumn && pathColumn.columnType !== ColumnTypeEnum.UUID) {
        return `${generatedId}|${node.value}-${pathColumn.columnId}`;
      }
      return generatedId;
    }, '');
  }

  return hierarchyPaths.map((path) => {
    const finalNode = path.nodes[path.nodes.length - 1]!;
    const finalPathColumn = hierarchyLevelColumns.find((col) => col.columnId === finalNode.columnId);

    return {
      id: finalPathColumn?.columnType === ColumnTypeEnum.UUID ? finalNode.value! : buildUniqueHierarchyNodeId(path),
      columnId: finalNode.columnId,
      columnValue: finalNode.value,
      ancestors: path.nodes.slice(0, -1).map((node, nodeIndex) => {
        const pathColumn = hierarchyLevelColumns.find((col) => col.columnId === node.columnId);

        return {
          id:
            pathColumn?.columnType === ColumnTypeEnum.UUID
              ? node.value!
              : buildUniqueHierarchyNodeId({ nodes: path.nodes.slice(0, nodeIndex + 1) }),
          name: '',
          type: '',
          columnId: node.columnId,
          columnValue: node.value,
        };
      }),
    };
  });
};

export const isValidAIPayload = (payload: Record<string, unknown>): payload is AIPayload => {
  const { propertySearches, fixedListSearches } = payload;
  if (!propertySearches && !fixedListSearches) {
    return false;
  }
  if (propertySearches && Array.isArray(propertySearches)) {
    if (!propertySearches.every(isPropertySearch)) {
      return false;
    }
  }
  if (fixedListSearches && Array.isArray(fixedListSearches)) {
    if (!fixedListSearches.every(isFixedListSearch)) {
      return false;
    }
  }
  return true;
};

export const isHierarchySearch = (search: unknown): search is HierarchySearch => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as HierarchySearchV1).searchType === SearchTypeEnum.HIERARCHY;
  return searchTypeCorrect && typeof search === 'object' && !!search && 'hierarchyId' in search;
};

export const isHierarchySearchOutput = (search: unknown): search is HierarchySearchOutputV1 => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as HierarchySearchOutputV1).searchType === SearchTypeEnum.HIERARCHY;
  return (
    searchTypeCorrect &&
    typeof search === 'object' &&
    !!search &&
    'hierarchy' in search &&
    search.hierarchy !== undefined
  );
};

export const isStoredTableSearch = (search: unknown): search is StoredTableSearch => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as StoredTableSearchV1).searchType === SearchTypeEnum.STOREDTABLE;
  return searchTypeCorrect && typeof search === 'object' && !!search && 'storedTableId' in search;
};

export const isScalingTableSearch = (search: unknown): search is ScalingTableSearch => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect =
    (search as ScalingTableUUIDColumnSearchV1).searchType === SearchTypeEnum.MATERIALIZEDTABLEUUIDCOLUMN;
  return searchTypeCorrect && 'tableDefinitionId' in search;
};

/**
 * Format scaling table searches for API submission
 * Filters to only valid searches (with both table and column) and maps to API format
 */
export const isSubmittableScalingTableSearch = (search: ScalingTableSearch): boolean =>
  !!(search.tableDefinitionId && search.columnDefinitionId);

export const scalingTableSearchToColumnSearchOutput = (
  searches: ScalingTableSearch[],
): ScalingTableUUIDColumnSearchV1[] => {
  return searches.filter(isSubmittableScalingTableSearch).map((search) => ({
    searchType: ScalingTableSearchTypeEnum.MATERIALIZEDTABLEUUIDCOLUMN,
    isInclude: search.isInclude,
    tableDefinitionId: search.tableDefinitionId!,
    columnDefinitionId: search.columnDefinitionId!,
    types: ['Condition', 'StoredCondition', 'CalculatedCondition'],
    rowIds: [],
    searchOrder: search.searchOrder,
    pathOverride: search.pathOverride,
  }));
};

const isFixedListSearch = (search: unknown): search is FixedListSearchV1 => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as models.FixedListSearchV1).searchType === SearchTypeEnum.FIXEDLIST;
  const isIncludeCorrect = typeof (search as models.FixedListSearchV1).isInclude === 'boolean';
  const itemIdsCorrect = Array.isArray((search as models.FixedListSearchV1).itemIds);
  return searchTypeCorrect && isIncludeCorrect && itemIdsCorrect;
};

const isPropertySearch = (search: unknown): search is PropertySearchV1 => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  if ((search as PropertySearchV1).searchType !== SearchTypeEnum.PROPERTY) {
    return false;
  }
  if (!('searchType' in search) || !('isInclude' in search) || !('types' in search)) {
    return false;
  }

  if ('predicates' in search) {
    if (!Array.isArray((search as PropertySearchV1).predicates)) {
      return false;
    }
    if (!(search as PropertySearchV1).predicates?.every(isPropertyFilterInputV1)) {
      return false;
    }
  }
  return true;
};

const isPropertyFilterInputV1 = (predicate: unknown): predicate is PropertyFilterInputV1 => {
  if (!predicate || typeof predicate !== 'object') {
    return false;
  }
  if (!('operator' in predicate) || !('propertyName' in predicate) || !('values' in predicate)) {
    return false;
  }
  if (!Array.isArray((predicate as PropertyFilterInputV1).values)) {
    return false;
  }
  return (predicate as PropertyFilterInputV1).values.every(isPropertyInputV1);
};

const isPropertyInputV1 = (value: unknown): value is PropertyInputV1 => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return 'value' in value;
};

export interface PartitionedItemFinderSearches {
  fixedListSearches: FixedListSearch[];
  propertySearches: Search[];
  hierarchySearches: HierarchySearch[];
  storedTableSearches: StoredTableSearch[];
  scalingTableSearches: ScalingTableSearch[];
}

/** Partitions store searches into the per-type lists the item finder APIs accept, preserving store order. */
export const partitionItemFinderSearches = (searches: Search[]): PartitionedItemFinderSearches => ({
  fixedListSearches: searches.filter(
    (search): search is FixedListSearch => search.searchType === SearchTypeEnum.FIXEDLIST,
  ),
  propertySearches: searches.filter(
    (search) =>
      search.searchType === SearchTypeEnum.PROPERTY ||
      (search.searchType as SearchType) === SearchTypeEnum.FOLDER ||
      (search.searchType as SearchType) === SearchTypeEnum.WORKBENCH ||
      (search.searchType as SearchType) === SearchTypeEnum.ASSET_TREE,
  ),
  hierarchySearches: searches.filter(isHierarchySearch),
  storedTableSearches: searches.filter(isStoredTableSearch),
  scalingTableSearches: searches.filter(isScalingTableSearch),
});

export interface ItemFinderSearchesPayload {
  fixedListSearches: FixedListSearchV1[];
  propertySearches: PropertySearchV1[];
  hierarchySearches: HierarchySearchV1[];
  storedTableSearches: StoredTableSearchV1[];
  scalingTableUUIDColumnSearches: ScalingTableUUIDColumnSearchV1[];
}

export function buildItemFinderSearchesPayload(
  searches: Search[],
  options: {
    propertySearchTypes?: string[];
    hiddenExclusionPredicates: PropertyFilterInputV1[];
    formatAdditionalPropertySearches?: (searches: Search[]) => PropertySearchV1[];
  },
): ItemFinderSearchesPayload {
  const partitioned = partitionItemFinderSearches(searches);
  return {
    fixedListSearches: formatFixedListSearches(partitioned.fixedListSearches) ?? [],
    propertySearches: [
      ...(formatPropertySearches(partitioned.propertySearches, {
        types: options.propertySearchTypes,
        hiddenExclusionPredicates: options.hiddenExclusionPredicates,
      }) ?? []),
      ...(options.formatAdditionalPropertySearches?.(searches) ?? []),
    ],
    hierarchySearches: formatHierarchySearches(partitioned.hierarchySearches, options.hiddenExclusionPredicates) ?? [],
    storedTableSearches: formatStoredTableSearches(partitioned.storedTableSearches, options.hiddenExclusionPredicates),
    scalingTableUUIDColumnSearches: scalingTableSearchToColumnSearchOutput(partitioned.scalingTableSearches),
  };
}
